/**
 * WelcomeTour Component
 * 
 * A guided tour for first-time users that highlights key features:
 * - Upload Content
 * - MatchFlow Builder
 * - Gallery
 * 
 * Shows only once per user (stored in localStorage)
 */

import React, { useState, useEffect, useCallback } from "react";

// ============================================================
// STORAGE KEY
// ============================================================
const TOUR_COMPLETED_KEY = "venueflow_tour_completed";

// ============================================================
// TOUR STEPS
// ============================================================
const TOUR_STEPS = [
  {
    id: "welcome",
    target: null, // Full screen overlay
    title: "Welcome to",
    subtitle: "Lumen8 VenueFlow 👋",
    description: "Let's take a quick tour to help you get started. This will only take a moment.",
    position: "center",
    emoji: "👋",
  },
  {
    id: "upload",
    target: "[data-tour='upload']",
    title: "Upload Content",
    description: "Start here. Upload your graphics, videos, and assets for LED, screens, and IPTV across venues.",
    position: "right",
    emoji: "📤",
  },
  {
    id: "matchflow",
    target: "[data-tour='matchflow']",
    title: "MatchFlow Builder",
    description: "Next, schedule and allocate your uploaded content across fixtures and match phases. Add content to simple rows, and once approved, it is automatically scheduled for delivery.",
    position: "right",
    emoji: "📅",
  },
  {
    id: "gallery",
    target: "[data-tour='gallery']",
    title: "MatchFlow Library",
    description: "View all uploaded assets in one place. Download files or copy Content IDs to reference when scheduling content in MatchFlow. Content can be allocated using either the Content ID or original file name.",
    position: "right",
    emoji: "🖼️",
  },
  {
    id: "content-id",
    target: null, // Full screen overlay - no highlight needed
    title: "Your Lumen8 Content ID",
    description: "Each uploaded asset is automatically assigned a unique Content ID (e.g. STK_348JJR). Use this ID to track your content or reference it with your Lumen8 Coordinator.",
    position: "center",
    emoji: "🏷️",
  },
  {
    id: "complete",
    target: null, // Full screen overlay
    title: "You're all set! 🎉",
    description: "That's the basics! For more in-depth training, contact your Lumen8 Coordination team or use the help icon in the bottom-right of your screen for getting-started tips.",
    position: "center",
    emoji: "🎉",
  },
];

// ============================================================
// THEME
// ============================================================
const THEME = {
  bg: "#0a0a0f",
  bgElevated: "#16161f",
  accent: "#2fd6b5",
  accentGlow: "rgba(47, 214, 181, 0.15)",
  lime: "#cdde25",
  textPrimary: "#ffffff",
  textSecondary: "#a1a1aa",
  border: "rgba(255, 255, 255, 0.1)",
  overlay: "rgba(0, 0, 0, 0.85)",
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: THEME.overlay,
    zIndex: 100000,
    transition: "opacity 0.3s ease",
  },
  
  spotlight: {
    position: "fixed",
    borderRadius: 12,
    boxShadow: `0 0 0 9999px ${THEME.overlay}, 0 0 30px ${THEME.accent}`,
    zIndex: 100001,
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    pointerEvents: "none",
  },
  
  tooltip: {
    position: "fixed",
    backgroundColor: THEME.bgElevated,
    borderRadius: 16,
    padding: "24px 28px",
    maxWidth: 340,
    boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px ${THEME.border}`,
    zIndex: 100002,
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  
  tooltipCenter: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    maxWidth: 420,
  },
  
  arrow: {
    position: "absolute",
    width: 0,
    height: 0,
    borderStyle: "solid",
  },
  
  arrowLeft: {
    left: -10,
    top: "50%",
    transform: "translateY(-50%)",
    borderWidth: "10px 10px 10px 0",
    borderColor: `transparent ${THEME.bgElevated} transparent transparent`,
  },
  
  stepIndicator: {
    display: "flex",
    gap: 6,
    justifyContent: "center",
    marginBottom: 20,
  },
  
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: THEME.border,
    transition: "all 0.3s ease",
  },
  
  stepDotActive: {
    backgroundColor: THEME.accent,
    transform: "scale(1.2)",
  },
  
  stepDotCompleted: {
    backgroundColor: THEME.accent,
    opacity: 0.5,
  },
  
  emoji: {
    fontSize: 48,
    marginBottom: 16,
    display: "block",
  },
  
  logo: {
    width: 64,
    height: 64,
    marginBottom: 20,
    objectFit: "contain",
  },
  
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: THEME.textPrimary,
    marginBottom: 4,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  
  titleSubtitle: {
    fontSize: 26,
    fontWeight: 700,
    color: THEME.accent,
    marginBottom: 16,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  
  description: {
    fontSize: 15,
    color: THEME.textSecondary,
    lineHeight: 1.6,
    marginBottom: 24,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  
  buttons: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
  },
  
  buttonPrimary: {
    padding: "12px 24px",
    fontSize: 14,
    fontWeight: 600,
    background: `linear-gradient(135deg, ${THEME.accent} 0%, ${THEME.lime} 100%)`,
    color: THEME.bg,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Grotesk', sans-serif",
    transition: "all 0.2s ease",
  },
  
  buttonSecondary: {
    padding: "12px 24px",
    fontSize: 14,
    fontWeight: 500,
    background: "transparent",
    color: THEME.textSecondary,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Grotesk', sans-serif",
    transition: "all 0.2s ease",
  },
  
  skipButton: {
    position: "fixed",
    top: 24,
    right: 24,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    background: "rgba(255, 255, 255, 0.1)",
    color: THEME.textSecondary,
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "'Space Grotesk', sans-serif",
    zIndex: 100003,
    transition: "all 0.2s ease",
  },
};

// ============================================================
// WELCOME TOUR COMPONENT
// ============================================================
export function WelcomeTour({ onComplete, forceShow = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Check if tour should show
  useEffect(() => {
    console.log("WelcomeTour useEffect running, forceShow:", forceShow);
    
    // Always check localStorage first
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY);
    console.log("Tour completed in localStorage:", completed);
    
    if (completed === "true") {
      console.log("Tour already completed, not showing");
      return;
    }

    // Show tour immediately (removed delay that was causing issues)
    console.log("Setting isVisible to true");
    setIsVisible(true);
  }, [forceShow]);

  // Update target element position
  useEffect(() => {
    if (!isVisible || isFadingOut) return;

    const step = TOUR_STEPS[currentStep];
    if (!step.target) {
      setTargetRect(null);
      return;
    }

    const updatePosition = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        });
      }
    };

    // Initial position
    setIsAnimating(true);
    setTimeout(() => {
      updatePosition();
      setIsAnimating(false);
    }, 50);

    // Update on resize
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [currentStep, isVisible, isFadingOut]);

  // Complete tour with fade out
  const completeTour = useCallback(() => {
    console.log("=== TOUR COMPLETING ===");
    console.log("Setting localStorage...");
    localStorage.setItem(TOUR_COMPLETED_KEY, "true");
    console.log("localStorage set, now fading out...");
    setIsFadingOut(true);
    
    // Wait for fade out animation then hide
    const timer = setTimeout(() => {
      console.log("Fade complete, setting isVisible to false...");
      setIsVisible(false);
      setIsFadingOut(false);
      console.log("Tour should now be hidden!");
      if (onComplete) {
        console.log("Calling onComplete callback");
        onComplete();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [onComplete]);

  // Next step
  const nextStep = useCallback(() => {
    try {
      console.log(`Next step called. Current: ${currentStep}, Total: ${TOUR_STEPS.length}, IsLast: ${currentStep >= TOUR_STEPS.length - 1}`);
      
      if (currentStep < TOUR_STEPS.length - 1) {
        console.log("Not last step, advancing...");
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentStep((prev) => {
            const newStep = prev + 1;
            console.log(`Setting step from ${prev} to ${newStep}`);
            console.log(`Next step data:`, TOUR_STEPS[newStep]);
            return newStep;
          });
          // Reset animation state after step change
          setTimeout(() => setIsAnimating(false), 50);
        }, 100);
      } else {
        console.log("This is the last step, calling completeTour...");
        completeTour();
      }
    } catch (error) {
      console.error("Error in nextStep:", error);
    }
  }, [currentStep, completeTour]);

  // Previous step
  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1);
        setTimeout(() => setIsAnimating(false), 50);
      }, 100);
    }
  }, [currentStep]);

  // Skip tour
  const skipTour = useCallback(() => {
    completeTour();
  }, [completeTour]);

  console.log("WelcomeTour render - isVisible:", isVisible, "isFadingOut:", isFadingOut);

  if (!isVisible) {
    console.log("Not rendering: isVisible is false");
    return null;
  }
  
  // Extra safety check - if localStorage says completed, don't render
  const lsCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);
  if (lsCompleted === "true") {
    console.log("Not rendering: localStorage says completed");
    return null;
  }

  const step = TOUR_STEPS[currentStep];
  console.log("Rendering step:", currentStep, step?.id);
  
  const isCenter = step.position === "center";
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (isCenter || !targetRect) {
      return styles.tooltipCenter;
    }

    // Position to the right of the target
    return {
      top: targetRect.top + targetRect.height / 2,
      left: targetRect.left + targetRect.width + 20,
      transform: "translateY(-50%)",
    };
  };

  return (
    <>
      {/* Dark overlay */}
      <div style={{
        ...styles.overlay,
        opacity: isFadingOut ? 0 : 1,
        pointerEvents: isFadingOut ? "none" : "auto",
      }} />

      {/* Spotlight on target element */}
      {targetRect && !isFadingOut && (
        <div
          style={{
            ...styles.spotlight,
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            opacity: isAnimating ? 0 : 1,
          }}
        />
      )}

      {/* Skip button */}
      {!isLastStep && !isFadingOut && (
        <button
          style={styles.skipButton}
          onClick={skipTour}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.color = THEME.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = THEME.textSecondary;
          }}
        >
          Skip Tour
        </button>
      )}

      {/* Tooltip */}
      <div
        style={{
          ...styles.tooltip,
          ...getTooltipStyle(),
          opacity: isAnimating || isFadingOut ? 0 : 1,
        }}
      >
        {/* Arrow (for non-center tooltips) */}
        {!isCenter && targetRect && (
          <div style={{ ...styles.arrow, ...styles.arrowLeft }} />
        )}

        {/* Step indicator dots */}
        <div style={styles.stepIndicator}>
          {TOUR_STEPS.map((_, index) => (
            <div
              key={index}
              style={{
                ...styles.stepDot,
                ...(index === currentStep ? styles.stepDotActive : {}),
                ...(index < currentStep ? styles.stepDotCompleted : {}),
              }}
            />
          ))}
        </div>

        {/* Emoji for each step */}
        {step.emoji && (
          <div style={{ 
            fontSize: isCenter ? 56 : 40,
            marginBottom: isCenter ? 16 : 12,
            display: "flex",
            justifyContent: "center",
          }}>
            {step.emoji}
          </div>
        )}

        {/* Title */}
        <h3 style={{
          ...styles.title,
          marginBottom: step.subtitle ? 4 : 12,
        }}>{step.title}</h3>
        
        {/* Subtitle (for welcome screen) */}
        {step.subtitle && (
          <h3 style={styles.titleSubtitle}>{step.subtitle}</h3>
        )}

        {/* Description */}
        <p style={styles.description}>{step.description}</p>

        {/* Buttons */}
        <div style={styles.buttons}>
          {!isFirstStep && !isLastStep && (
            <button
              style={styles.buttonSecondary}
              onClick={prevStep}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = THEME.accent;
                e.currentTarget.style.color = THEME.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = THEME.border;
                e.currentTarget.style.color = THEME.textSecondary;
              }}
            >
              Back
            </button>
          )}

          <button
            style={styles.buttonPrimary}
            onClick={nextStep}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 20px rgba(47, 214, 181, 0.3)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {isFirstStep ? "Start Tour" : isLastStep ? "Get Started" : "Next"}
          </button>
        </div>

        {/* Don't show again option - only on first step */}
        {isFirstStep && (
          <button
            style={{
              marginTop: 16,
              padding: "8px 16px",
              fontSize: 13,
              color: THEME.textMuted,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Space Grotesk', sans-serif",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
            onClick={skipTour}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = THEME.textSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = THEME.textMuted;
            }}
          >
            Don't show this again
          </button>
        )}
      </div>
    </>
  );
}

// ============================================================
// HOOK TO RESET TOUR (for testing)
// ============================================================
export function useResetTour() {
  return useCallback(() => {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    window.location.reload();
  }, []);
}

// ============================================================
// CHECK IF TOUR COMPLETED
// ============================================================
export function isTourCompleted() {
  return localStorage.getItem(TOUR_COMPLETED_KEY) === "true";
}

export default WelcomeTour;
